import { useCallback, useEffect, useState } from "react";
import type { ITransaction } from "../interfaces/transactions";
import { TransactionsStatus } from "../utils/commons";
import {
  resolveSendReceiptId,
  sendMockReceipts,
  sendMockTransactions,
  type SendMockReceipt,
} from "./sendMock";

const LIVE_OP_ID = "op_gallery_send_processing";
const TICK_MS = 3000;

type LiveReceiptStore = Record<string, SendMockReceipt>;

let receiptStore: LiveReceiptStore = { ...sendMockReceipts };
const receiptListeners = new Set<() => void>();

function notifyReceiptListeners(): void {
  receiptListeners.forEach((listener) => listener());
}

/**
 * Gallery-only simulation: the processing row transitions to completed after
 * ~3s so Control Dock demos live status updates without a real SSE backend.
 */
export function useSendLiveOperations(): ITransaction[] {
  const [items, setItems] = useState<ITransaction[]>(() =>
    sendMockTransactions.map((item) => ({ ...item }))
  );

  useEffect(() => {
    const id = window.setInterval(() => {
      setItems((prev) =>
        prev.map((item) => {
          if (item.operationId !== LIVE_OP_ID) return item;
          if (item.status === TransactionsStatus.COMPLETED) return item;

          receiptStore = {
            ...receiptStore,
            [LIVE_OP_ID]: {
              ...receiptStore[LIVE_OP_ID],
              status: "completed",
            },
          };
          notifyReceiptListeners();

          return { ...item, status: TransactionsStatus.COMPLETED };
        })
      );
    }, TICK_MS);

    return () => window.clearInterval(id);
  }, []);

  return items;
}

/** Live receipt for gallery demo; shares state with useSendLiveOperations. */
export function useSendLiveReceipt(
  paramId: string | undefined,
  pathname: string
): SendMockReceipt {
  const [, bump] = useState(0);
  const forceUpdate = useCallback(() => bump((n) => n + 1), []);

  useEffect(() => {
    receiptListeners.add(forceUpdate);
    return () => {
      receiptListeners.delete(forceUpdate);
    };
  }, [forceUpdate]);

  const id = resolveSendReceiptId(paramId, pathname);
  return receiptStore[id] ?? sendMockReceipts[id];
}
